import GokuDataGrid from '@/components/GokuDataGrid'
import SubHeaders from '@/components/Headers/SubHeaders'
import { useModal } from '@/contexts/modal'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import CheckCircleIcons from '@/icons/CheckCircleIcons'
import CancelIcons from '@/icons/CancelIcons'
import useAuthStore from '@/stores/useAuthStore'
import { IPermissionProps } from '@/types/shared'
import { paths } from '@/utils/path'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { IAction } from 'vnetwork-ui/dist/components/GokuDataGrid/Actions'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { DocumentModalProps } from '@/modals/DocumentModal'
import {
    approveShareRequest,
    getDocumentById,
    listShareRequests,
    rejectShareRequest,
} from '../../services'
import { DocumentShareRequest } from '../../types/reponse'
import Filters from '../shared/Filters'
import { shareRequestColumns } from './columns'

const ApproveDocument: React.FC<IPermissionProps> = () => {
    const { t } = useTranslation()
    const { setModal } = useModal()
    const { employee } = useAuthStore()
    const { refetch: refetchShareRequests } = useQueryClientCallback({
        pattern: 'document-shared-with-me',
        mode: 'contains',
    })
    const { refetch: refetchSharedWithMe } = useQueryClientCallback({
        pattern: QUERY_KEYS.SHARED_WITH_ME,
        mode: 'contains',
    })

    const { mutateAsync: approveShareRequestMutation } = useMutationWithHandler(
        {
            mutationFn: (requestId: number) => approveShareRequest(requestId),
        },
        {
            reactQueryKeys: QUERY_KEYS.DOCUMENTS,
            successMessage: t('approve_success'),
            errorMessage: t('approve_error'),
            onSuccess: () => {
                refetchShareRequests()
                refetchSharedWithMe()
            },
        }
    )

    const { mutateAsync: rejectShareRequestMutation } = useMutationWithHandler(
        {
            mutationFn: (requestId: number) => rejectShareRequest(requestId),
        },
        {
            reactQueryKeys: QUERY_KEYS.DOCUMENTS,
            successMessage: t('reject_success'),
            errorMessage: t('reject_error'),
            onSuccess: () => {
                refetchShareRequests()
                refetchSharedWithMe()
            },
        }
    )

    const handleSelectDocument = async (row: DocumentShareRequest) => {
        try {
            const response = await getDocumentById(row.document_id)
            const document = response?.result

            if (!document) {
                toast.error(t('document_not_found'))
                return
            }

            setModal<DocumentModalProps>('DocumentModal/index', {
                title: document.name_file,
                description: document.descriptions,
                versions: document.versions ?? [],
            })
        } catch (_error) {
            toast.error(t('document_fetch_failed'))
        }
    }

    const handleAction = async (
        row: DocumentShareRequest,
        action: 'approve' | 'reject'
    ) => {
        if (row.status !== 'PENDING') {
            toast.error(t('request_already_processed'))
            return
        }
        const isAssignable =
            !row.approver_ids?.length ||
            row.approver_ids.includes(employee?.employee_id ?? -1)

        if (!isAssignable) {
            toast.error(
                action === 'approve'
                    ? t('no_permission_approve')
                    : t('no_permission_reject')
            )
            return
        }

        if (action === 'approve') {
            await approveShareRequestMutation(row.id)
            return
        }

        await rejectShareRequestMutation(row.id)
    }

    const dataGridActions: IAction<DocumentShareRequest>[] = [
        {
            onClick: (row) => handleAction(row, 'approve'),
            title: t('approve'),
            color: 'success',
            icon: <CheckCircleIcons />,
        },
        {
            onClick: (row) => handleAction(row, 'reject'),
            title: t('reject'),
            color: 'error',
            icon: <CancelIcons />,
        },
    ]

    return (
        <>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.documentApproved,
                        title: t('approved_documents'),
                    },
                ]}
            />
            <div className="my-2">
                <GokuDataGrid
                    apiCall={(payload) => {
                        console.log('payload', payload)
                        return listShareRequests(payload)
                    }}
                    reactQueryKey={QUERY_KEYS.DOCUMENTS_SHARE_REQUESTS}
                    columns={shareRequestColumns(t, handleSelectDocument)}
                    actions={dataGridActions}
                    options={{
                        showSearch: true,
                        showHideColumn: true,
                        pageSize: 10,
                        title: '',
                    }}
                    searchPlaceholder={t('search_share_requests')}
                    dataGridProps={{
                        getRowId: (row) => row?.document_id,
                    }}
                    filterDefaultValues={{
                        employee_id: [],
                        document_type: [],
                        document_level: [],
                        start_day: [],
                        end_day: [],
                        status: '',
                        approver_id: [],
                        requester_id: [],
                        document_id: [],
                    }}
                >
                    {({ control }) => (
                        <Filters control={control} showStatusFilter />
                    )}
                </GokuDataGrid>
            </div>
        </>
    )
}
export default ApproveDocument
